/**
 * Currency utilities — Stripe decimal handling, symbols, country mapping, formatting.
 * Ported from PopshopRN/src/flows/bio/functions/currency.ts
 */
import currency from 'currency.js';
import invariant from 'tiny-invariant';

// ─── Stripe zero-decimal currencies ────────────────────────────────────────
// https://docs.stripe.com/currencies#presentment-currencies

export const ZERO_DECIMAL_CURRENCY_CODES = [
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
] as const;

// https://docs.stripe.com/currencies#special-cases
export const ZERO_DECIMAL_CURRENCY_CODES_FOR_PAYOUT = [
  'ISK', // Icelandic Króna
  'HUF', // Hungarian Forint
  'TWD', // New Taiwan Dollar
  'UGX', // Ugandan Shilling
] as const;

// https://support.stripe.com/questions/which-payments-methods-and-products-are-available-in-the-uae
const THREE_DECIMAL_CURRENCY_CODES = [
  'BHD', // Bahraini dinar
  'JOD', // Jordanian dinar
  'KWD', // Kuwaiti dinar
  'OMR', // Omani rial
  'TND', // Tunisian dinar
] as const;

// ─── Currency symbols ──────────────────────────────────────────────────────

const CURRENCY_SYMBOLS = new Map([
  ['AED', 'د.إ'],
  ['AFN', '؋'],
  ['ALL', 'L'],
  ['AMD', '֏'],
  ['AOA', 'Kz'],
  ['ARS', '$'],
  ['AUD', 'A$'],
  ['AWG', 'Afl.'],
  ['AZN', '₼'],
  ['BAM', 'KM'],
  ['BBD', 'Bds$'],
  ['BDT', '৳'],
  ['BGN', 'лв'],
  ['BHD', '.د.ب'],
  ['BIF', 'FBu'],
  ['BMD', 'BD$'],
  ['BND', 'B$'],
  ['BOB', 'Bs.'],
  ['BRL', 'R$'],
  ['BSD', 'B$'],
  ['BTN', 'Nu.'],
  ['BWP', 'P'],
  ['BYN', 'Br'],
  ['BZD', 'BZ$'],
  ['CAD', 'C$'],
  ['CDF', 'FC'],
  ['CHF', 'CHF'],
  ['CLP', '$'],
  ['CNY', '¥'],
  ['COP', '$'],
  ['CRC', '₡'],
  ['CVE', '$'],
  ['CZK', 'Kč'],
  ['DJF', 'Fdj'],
  ['DKK', 'kr'],
  ['DOP', 'RD$'],
  ['DZD', 'دج'],
  ['EGP', 'E£'],
  ['ERN', 'Nfk'],
  ['ETB', 'Br'],
  ['EUR', '€'],
  ['FJD', 'FJ$'],
  ['FKP', '£'],
  ['GBP', '£'],
  ['GEL', '₾'],
  ['GHS', '₵'],
  ['GIP', '£'],
  ['GMD', 'D'],
  ['GNF', 'FG'],
  ['GTQ', 'Q'],
  ['GYD', 'G$'],
  ['HKD', 'HK$'],
  ['HRK', 'kn'],
  ['HTG', 'G'],
  ['HUF', 'Ft'],
  ['IDR', 'Rp'],
  ['ILS', '₪'],
  ['INR', '₹'],
  ['IQD', 'ع.د'],
  ['IRR', '﷼'],
  ['ISK', 'kr'],
  ['JMD', 'J$'],
  ['JOD', 'JD'],
  ['JPY', '¥'],
  ['KES', 'KSh'],
  ['KGS', 'с'],
  ['KHR', '៛'],
  ['KMF', 'CF'],
  ['KPW', '₩'],
  ['KRW', '₩'],
  ['KWD', 'د.ك'],
  ['KYD', 'CI$'],
  ['KZT', '₸'],
  ['LAK', '₭'],
  ['LBP', '£L'],
  ['LKR', '₨'],
  ['LRD', 'L$'],
  ['LSL', 'L'],
  ['LYD', 'ل.د'],
  ['MAD', 'د.م.'],
  ['MDL', 'L'],
  ['MGA', 'Ar'],
  ['MKD', 'ден'],
  ['MMK', 'K'],
  ['MNT', '₮'],
  ['MOP', 'MOP$'],
  ['MRU', 'UM'],
  ['MUR', '₨'],
  ['MVR', 'Rf'],
  ['MWK', 'MK'],
  ['MXN', '$'],
  ['MYR', 'RM'],
  ['MZN', 'MT'],
  ['NAD', 'N$'],
  ['NGN', '₦'],
  ['NIO', 'C$'],
  ['NOK', 'kr'],
  ['NPR', '₨'],
  ['NZD', 'NZ$'],
  ['OMR', '﷼'],
  ['PAB', 'B/.'],
  ['PEN', 'S/'],
  ['PGK', 'K'],
  ['PHP', '₱'],
  ['PKR', '₨'],
  ['PLN', 'zł'],
  ['PYG', '₲'],
  ['QAR', '﷼'],
  ['RON', 'lei'],
  ['RSD', 'дин.'],
  ['RUB', '₽'],
  ['RWF', 'R₣'],
  ['SAR', '﷼'],
  ['SBD', 'SI$'],
  ['SCR', 'SR'],
  ['SDG', 'ج.س.'],
  ['SEK', 'kr'],
  ['SGD', 'S$'],
  ['SHP', '£'],
  ['SLL', 'Le'],
  ['SOS', 'Sh'],
  ['SRD', '$'],
  ['SSP', '£'],
  ['STN', 'Db'],
  ['SYP', '£S'],
  ['SZL', 'E'],
  ['THB', '฿'],
  ['TJS', 'ЅМ'],
  ['TMT', 'm'],
  ['TND', 'د.ت'],
  ['TOP', 'T$'],
  ['TRY', '₺'],
  ['TTD', 'TT$'],
  ['TWD', 'NT$'],
  ['TZS', 'TSh'],
  ['UAH', '₴'],
  ['UGX', 'USh'],
  ['USD', '$'],
  ['UYU', '$U'],
  ['UZS', 'лв'],
  ['VEF', 'Bs.'],
  ['VND', '₫'],
  ['WST', 'WS$'],
  ['XAF', 'FCFA'],
  ['XCD', 'EC$'],
  ['XOF', 'CFA'],
  ['XPF', '₣'],
  ['YER', '﷼'],
  ['ZAR', 'R'],
  ['ZMW', 'ZK'],
  ['ZWL', 'Z$'],
]);

// ─── Country → currency mapping ────────────────────────────────────────────

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  AD: 'EUR',
  AE: 'AED',
  AF: 'AFN',
  AG: 'XCD',
  AI: 'XCD',
  AL: 'ALL',
  AM: 'AMD',
  AN: 'ANG',
  AO: 'AOA',
  AQ: 'USD',
  AR: 'ARS',
  AS: 'USD',
  AT: 'EUR',
  AU: 'AUD',
  AW: 'AWG',
  AX: 'EUR',
  AZ: 'AZN',
  BA: 'BAM',
  BB: 'BBD',
  BD: 'BDT',
  BE: 'EUR',
  BF: 'XOF',
  BG: 'BGN',
  BH: 'BHD',
  BI: 'BIF',
  BJ: 'XOF',
  BL: 'EUR',
  BM: 'BMD',
  BN: 'BND',
  BO: 'BOB',
  BQ: 'USD',
  BR: 'BRL',
  BS: 'BSD',
  BT: 'BTN',
  BV: 'NOK',
  BW: 'BWP',
  BY: 'BYN',
  BZ: 'BZD',
  CA: 'CAD',
  CC: 'AUD',
  CD: 'CDF',
  CF: 'XAF',
  CG: 'XAF',
  CH: 'CHF',
  CI: 'XOF',
  CK: 'NZD',
  CL: 'CLP',
  CM: 'XAF',
  CN: 'CNY',
  CO: 'COP',
  CR: 'CRC',
  CU: 'CUP',
  CV: 'CVE',
  CW: 'ANG',
  CX: 'AUD',
  CY: 'EUR',
  CZ: 'CZK',
  DE: 'EUR',
  DJ: 'DJF',
  DK: 'DKK',
  DM: 'XCD',
  DO: 'DOP',
  DZ: 'DZD',
  EC: 'USD',
  EE: 'EUR',
  EG: 'EGP',
  EH: 'MAD',
  ER: 'ERN',
  ES: 'EUR',
  ET: 'ETB',
  FI: 'EUR',
  FJ: 'FJD',
  FK: 'FKP',
  FM: 'USD',
  FO: 'DKK',
  FR: 'EUR',
  GA: 'XAF',
  GB: 'GBP',
  GD: 'XCD',
  GE: 'GEL',
  GF: 'EUR',
  GG: 'GBP',
  GH: 'GHS',
  GI: 'GIP',
  GL: 'DKK',
  GM: 'GMD',
  GN: 'GNF',
  GP: 'EUR',
  GQ: 'XAF',
  GR: 'EUR',
  GS: 'FKP',
  GT: 'GTQ',
  GU: 'USD',
  GW: 'XOF',
  GY: 'GYD',
  HK: 'HKD',
  HM: 'AUD',
  HN: 'HNL',
  HR: 'EUR',
  HT: 'HTG',
  HU: 'HUF',
  ID: 'IDR',
  IE: 'EUR',
  IL: 'ILS',
  IM: 'GBP',
  IN: 'INR',
  IO: 'USD',
  IQ: 'IQD',
  IR: 'IRR',
  IS: 'ISK',
  IT: 'EUR',
  JE: 'GBP',
  JM: 'JMD',
  JO: 'JOD',
  JP: 'JPY',
  KE: 'KES',
  KG: 'KGS',
  KH: 'KHR',
  KI: 'AUD',
  KM: 'KMF',
  KN: 'XCD',
  KP: 'KPW',
  KR: 'KRW',
  KW: 'KWD',
  KY: 'KYD',
  KZ: 'KZT',
  LA: 'LAK',
  LB: 'LBP',
  LC: 'XCD',
  LI: 'CHF',
  LK: 'LKR',
  LR: 'LRD',
  LS: 'LSL',
  LT: 'EUR',
  LU: 'EUR',
  LV: 'EUR',
  LY: 'LYD',
  MA: 'MAD',
  MC: 'EUR',
  MD: 'MDL',
  ME: 'EUR',
  MF: 'EUR',
  MG: 'MGA',
  MH: 'USD',
  MK: 'MKD',
  ML: 'XOF',
  MM: 'MMK',
  MN: 'MNT',
  MO: 'MOP',
  MP: 'USD',
  MQ: 'EUR',
  MR: 'MRU',
  MS: 'XCD',
  MT: 'EUR',
  MU: 'MUR',
  MV: 'MVR',
  MW: 'MWK',
  MX: 'MXN',
  MY: 'MYR',
  MZ: 'MZN',
  NA: 'NAD',
  NC: 'XPF',
  NE: 'XOF',
  NF: 'AUD',
  NG: 'NGN',
  NI: 'NIO',
  NL: 'EUR',
  NO: 'NOK',
  NP: 'NPR',
  NR: 'AUD',
  NU: 'NZD',
  NZ: 'NZD',
  OM: 'OMR',
  PA: 'PAB',
  PE: 'PEN',
  PF: 'XPF',
  PG: 'PGK',
  PH: 'PHP',
  PK: 'PKR',
  PL: 'PLN',
  PM: 'EUR',
  PN: 'NZD',
  PR: 'USD',
  PS: 'ILS',
  PT: 'EUR',
  PW: 'USD',
  PY: 'PYG',
  QA: 'QAR',
  RE: 'EUR',
  RO: 'RON',
  RS: 'RSD',
  RU: 'RUB',
  RW: 'RWF',
  SA: 'SAR',
  SB: 'SBD',
  SC: 'SCR',
  SD: 'SDG',
  SE: 'SEK',
  SG: 'SGD',
  SH: 'SHP',
  SI: 'EUR',
  SJ: 'NOK',
  SK: 'EUR',
  SL: 'SLE',
  SM: 'EUR',
  SN: 'XOF',
  SO: 'SOS',
  SR: 'SRD',
  SS: 'SSP',
  ST: 'STN',
  SV: 'USD',
  SX: 'ANG',
  SY: 'SYP',
  SZ: 'SZL',
  TC: 'USD',
  TD: 'XAF',
  TF: 'EUR',
  TG: 'XOF',
  TH: 'THB',
  TJ: 'TJS',
  TK: 'NZD',
  TL: 'USD',
  TM: 'TMT',
  TN: 'TND',
  TO: 'TOP',
  TR: 'TRY',
  TT: 'TTD',
  TV: 'AUD',
  TW: 'TWD',
  TZ: 'TZS',
  UA: 'UAH',
  UG: 'UGX',
  UM: 'USD',
  US: 'USD',
  UY: 'UYU',
  UZ: 'UZS',
  VA: 'EUR',
  VC: 'XCD',
  VE: 'VED',
  VG: 'USD',
  VI: 'USD',
  VN: 'VND',
  VU: 'VUV',
  WF: 'XPF',
  WS: 'WST',
  XK: 'EUR',
  YE: 'YER',
  YT: 'EUR',
  ZA: 'ZAR',
  ZM: 'ZMW',
  ZW: 'ZWG',
};

// ─── Minimum charges per currency ──────────────────────────────────────────

export const MINIMUM_CHARGES_BY_CURRENCY: Record<string, number> = {
  USD: 0.5,
  AED: 2.0,
  AUD: 0.5,
  BGN: 1.0,
  BRL: 0.5,
  CAD: 0.5,
  CHF: 0.5,
  CZK: 15.0,
  DKK: 2.5,
  EUR: 0.5,
  GBP: 0.3,
  HKD: 4.0,
  HUF: 175.0,
  INR: 0.5,
  JPY: 50,
  MXN: 10,
  MYR: 2,
  NOK: 3.0,
  NZD: 0.5,
  PLN: 2.0,
  RON: 2.0,
  SEK: 3.0,
  SGD: 0.5,
  THB: 10,
};

// ─── Lookup sets ───────────────────────────────────────────────────────────

const zeroDecimalSet: ReadonlySet<string> = new Set(ZERO_DECIMAL_CURRENCY_CODES);
const threeDecimalSet: ReadonlySet<string> = new Set(THREE_DECIMAL_CURRENCY_CODES);
const zeroDecimalPayoutSet: ReadonlySet<string> = new Set(ZERO_DECIMAL_CURRENCY_CODES_FOR_PAYOUT);

// ─── Decimal classification ────────────────────────────────────────────────

export function isZeroDecimalCurrency(currencyCode: string): boolean {
  return zeroDecimalSet.has(currencyCode.toUpperCase());
}

export function isZeroDecimalCurrencyForPayout(currencyCode: string): boolean {
  const upper = currencyCode.toUpperCase();
  return zeroDecimalSet.has(upper) || zeroDecimalPayoutSet.has(upper);
}

export function isThreeDecimalCurrency(currencyCode: string): boolean {
  return threeDecimalSet.has(currencyCode.toUpperCase());
}

// ─── Minimum charge ────────────────────────────────────────────────────────

export function minimumChargeOfCurrency(currencyCode: string): number {
  return MINIMUM_CHARGES_BY_CURRENCY[currencyCode.toUpperCase()] ?? 0.5;
}

// ─── Amount → minor unit conversion (for Stripe charges) ──────────────────

export function chargeAmountToMinorUnit(
  amount: number,
  currencyCode: string,
): number {
  if (isZeroDecimalCurrency(currencyCode)) return amount;
  if (isThreeDecimalCurrency(currencyCode)) return Math.round(amount * 1000);
  return Math.round(amount * 100);
}

export function chargeAmountToMinorUnitOrMinimum(
  amount: number | undefined,
  currencyCode: string,
): number {
  const value = amount ?? minimumChargeOfCurrency(currencyCode);
  return chargeAmountToMinorUnit(value, currencyCode);
}

export function payoutAmountToMinorUnit(
  amount: number,
  currencyCode: string,
): number {
  if (isZeroDecimalCurrencyForPayout(currencyCode)) return amount;
  if (isThreeDecimalCurrency(currencyCode)) return Math.round(amount * 1000);
  return Math.round(amount * 100);
}

// ─── Symbol & country lookup ───────────────────────────────────────────────

export function getCurrencySymbol(currencyCode: string): string {
  return (
    CURRENCY_SYMBOLS.get(currencyCode.toUpperCase()) ??
    currencyCode.toUpperCase()
  );
}

export function currencyOfCountry(
  countryCode: string,
  defaultCurrency = 'USD',
): string {
  invariant(
    countryCode.length === 2,
    'countryCode must be a 2-letter ISO 3166-1 alpha-2 code',
  );
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] ?? defaultCurrency;
}

// ─── Formatting ────────────────────────────────────────────────────────────

export type FormatMoneyOptions = currency.Options & { defaultText?: string };

/**
 * Format a monetary amount with the correct currency symbol using currency.js.
 */
export function formatMoney(
  amount: number | null | undefined,
  currencyCode: string,
  options?: FormatMoneyOptions,
): string {
  if (amount == null || isNaN(amount)) {
    return options?.defaultText ?? '--';
  }
  return currency(amount).format({
    symbol: getCurrencySymbol(currencyCode),
    ...options,
  });
}